import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS, apiRequest } from '@/lib/api';
import { DashboardType, DASHBOARD_NUMBERS } from '@/config/dashboardWorkflows';

export interface TaskData {
  id: number;
  stage?: number;
  status?: number;
  [key: string]: unknown;
}

interface UpdateTaskStageParams {
  taskId: number;
  stage: number;
  dashboardType?: DashboardType;
}

interface UpdateTaskStatusParams {
  taskId: number;
  status: number;
}

interface UpdateTaskKanbanColumnParams {
  taskId: number;
  kanbanColumn: number;
}

interface UpdateTaskSprintParams {
  taskId: number;
  sprintId: number | null;
  status?: number;
}

interface UpdateTaskSprintOrderParams {
  taskId: number;
  sprintOrder: number;
}

interface UpdateTaskOrderParams {
  taskId: number;
  order: number;
}

export function useTaskStageUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, stage, dashboardType }: UpdateTaskStageParams) => {
      if (!taskId || typeof taskId !== 'number') {
        throw new Error(`Invalid taskId: ${taskId}`);
      }

      const dashboardNum = dashboardType ? DASHBOARD_NUMBERS[dashboardType] : undefined;
      // The dashboard query parameter is REQUIRED for PATCH requests to dashboard tasks
      // because get_queryset() uses it to determine which queryset to search (dashboard vs project tasks)
      // Without it, dashboard tasks won't be found since they don't have a project
      const url = dashboardNum !== undefined
        ? `${API_ENDPOINTS.TASKS.LIST}${taskId}/?dashboard=${dashboardNum}`
        : `${API_ENDPOINTS.TASKS.LIST}${taskId}/`;

      const requestBody: Record<string, number> = { stage };
      if (dashboardNum !== undefined) {
        requestBody.dashboard = dashboardNum;
      }

      const response = await apiRequest(url, {
        method: 'PATCH',
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { detail: errorText || `HTTP ${response.status}` };
        }
        throw new Error(errorData.detail || `Failed to update task stage (${response.status})`);
      }

      return await response.json();
    },
    onMutate: async ({ taskId, stage, dashboardType }) => {
      if (!dashboardType) return;

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['dashboard', dashboardType] });

      // Snapshot previous value
      const previousData = queryClient.getQueryData<TaskData[]>(['dashboard', dashboardType]);

      // Optimistically update
      queryClient.setQueryData<TaskData[]>(['dashboard', dashboardType], (old) => {
        if (!old) return old;
        return old.map((task) =>
          task.id === taskId ? { ...task, stage } : task
        );
      });

      return { previousData };
    },
    onError: (_err, variables, context) => {
      // Rollback on error
      if (variables.dashboardType && context?.previousData) {
        queryClient.setQueryData(['dashboard', variables.dashboardType], context.previousData);
      }
    },
    onSuccess: (data, variables) => {
      // Update cache with server response on success
      if (variables.dashboardType && data) {
        queryClient.setQueryData<TaskData[]>(['dashboard', variables.dashboardType], (old) => {
          if (!old) return old;
          const updatedStage = data.stage !== undefined ? data.stage : variables.stage;
          return old.map((task) =>
            task.id === variables.taskId ? { ...task, stage: updatedStage } : task
          );
        });
      }
    },
  });
}

export function useTaskStatusUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, status }: UpdateTaskStatusParams) => {
      const response = await apiRequest(`${API_ENDPOINTS.TASKS.LIST}${taskId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          detail: 'Failed to update task status'
        }));
        throw new Error(errorData.detail || 'Failed to update task status');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate project task queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['project'] });
    },
  });
}

export function useTaskKanbanColumnUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, kanbanColumn }: UpdateTaskKanbanColumnParams) => {
      const response = await apiRequest(`${API_ENDPOINTS.TASKS.LIST}${taskId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ kanban_column: kanbanColumn }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          detail: 'Failed to update task kanban column'
        }));
        throw new Error(errorData.detail || 'Failed to update task kanban column');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project'] });
    },
  });
}

export function useTaskSprintUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, sprintId, status }: UpdateTaskSprintParams) => {
      const response = await apiRequest(`${API_ENDPOINTS.TASKS.LIST}${taskId}/`, {
        method: 'PATCH',
        body: JSON.stringify({
          sprint_id: sprintId,
          ...(status !== undefined && { status }),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          detail: 'Failed to update task sprint'
        }));
        throw new Error(errorData.detail || 'Failed to update task sprint');
      }

      return response.json();
    },
    onSuccess: () => {
      // Sprint changes only affect project views
      queryClient.invalidateQueries({ queryKey: ['project'] });
    },
  });
}

export function useTaskSprintOrderUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, sprintOrder }: UpdateTaskSprintOrderParams) => {
      const response = await apiRequest(`${API_ENDPOINTS.TASKS.LIST}${taskId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ sprint_order: sprintOrder }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          detail: 'Failed to update task sprint order'
        }));
        throw new Error(errorData.detail || 'Failed to update task sprint order');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project'] });
    },
  });
}

export function useTaskOrderUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, order }: UpdateTaskOrderParams) => {
      const response = await apiRequest(`${API_ENDPOINTS.TASKS.LIST}${taskId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ order }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          detail: 'Failed to update task order'
        }));
        throw new Error(errorData.detail || 'Failed to update task order');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project'] });
    },
  });
}

